import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `<ul class="layout-menu">
        @for (item of model; track item.label) {
            @if (!item.separator) {
                <li app-menuitem [item]="item" [root]="true"></li>
            } @else {
                <li class="menu-separator"></li>
            }
        }
    </ul> `
})
export class AppMenu {
    // EVERY signed-in user sees the whole menu ("show all the options to any user, not just admin
    // group", 2026-08-17). Navigation is not authorization: the API enforces every permission on
    // every request regardless of what the menu shows, so hiding entries only made the tool look
    // broken to non-admin accounts. A page a user cannot use answers with its own 403 message.
    readonly model: MenuItem[] = [
        { label: 'Home', items: [{ label: 'Overview', icon: 'pi pi-fw pi-home', routerLink: ['/'] }] },
        {
            label: 'Orders',
            items: [
                { label: 'Orders', icon: 'pi pi-fw pi-shopping-cart', routerLink: ['/orders'] },
                { label: 'Courier Costs', icon: 'pi pi-fw pi-dollar', routerLink: ['/courier-costs'] }
            ]
        },
        {
            label: 'Merchants',
            items: [
                { label: 'Merchants', icon: 'pi pi-fw pi-briefcase', routerLink: ['/merchants'] },
                { label: 'Locations', icon: 'pi pi-fw pi-map-marker', routerLink: ['/locations'] },
                // Store sign-ins (email + password) for the people at a location — not the Cognito sysadmins
                // under Administration.
                { label: 'Location users', icon: 'pi pi-fw pi-user', routerLink: ['/location-users'] },
                // Synthetic ohio liquor stores for dev/stress testing — middleware-only until
                // explicitly created in Magento.
                { label: 'Faux merchants', icon: 'pi pi-fw pi-sparkles', routerLink: ['/merchants/faux'] },
                { label: 'Payments', icon: 'pi pi-fw pi-credit-card', routerLink: ['/payments'] },
                { label: 'Payment Flows', icon: 'pi pi-fw pi-sync', routerLink: ['/payment-flows'] }
            ]
        },
        // ITS OWN SECTION, not an entry under Merchants. A machine is a real thing in a real shop whether or
        // not the platform has met that shop yet: one enrols itself before its store is onboarded, waits here
        // for a human, and can be approved and running with no merchant behind it at all.
        {
            label: 'Edge devices',
            items: [{ label: 'Devices', icon: 'pi pi-fw pi-desktop', routerLink: ['/edges'] }]
        },
        {
            label: 'Catalog',
            items: [
                { label: 'Products', icon: 'pi pi-fw pi-box', routerLink: ['/products'] },
                // Ohio's statewide list is not per-merchant, so it sits beside the per-location
                // catalog rather than inside it.
                { label: 'Ohio catalog', icon: 'pi pi-fw pi-list', routerLink: ['/ohlq'] }
            ]
        },
        {
            label: 'Administration',
            items: [
                { label: 'Payment Methods', icon: 'pi pi-fw pi-wallet', routerLink: ['/payment-methods'] },
                { label: 'Magento Instances', icon: 'pi pi-fw pi-server', routerLink: ['/instances'] },
                // Where online orders go - the store Edge or the legacy XT POS - and whether they are getting there.
                // Beside the XT connection because it is the switch between XT and its replacement.
                { label: 'Order hand-off', icon: 'pi pi-fw pi-directions', routerLink: ['/order-handoff'] },
                { label: 'XT Integration', icon: 'pi pi-fw pi-link', routerLink: ['/integrations/xt'] },
                // XT's own items table off the replica, per merchant — the screen to open when the
                // catalog projection and XT disagree. Next to the XT connection it comes from.
                { label: 'XT Items', icon: 'pi pi-fw pi-database', routerLink: ['/xt-items'] },
                // The middleware holds the DoorDash credentials (a quote must not be committed before
                // the payment is), so the connection, store ids and courier spend live here.
                { label: 'DoorDash', icon: 'pi pi-fw pi-truck', routerLink: ['/integrations/doordash'] },
                { label: 'Consumers', icon: 'pi pi-fw pi-bolt', routerLink: ['/consumers'] },
                { label: 'Users & Roles', icon: 'pi pi-fw pi-id-card', routerLink: ['/users'] },
                { label: 'Role Permissions', icon: 'pi pi-fw pi-sitemap', routerLink: ['/roles'] },
                { label: 'API Tokens', icon: 'pi pi-fw pi-key', routerLink: ['/tokens'] }
            ]
        }
    ];
}
